import omit from "lodash/omit";
import intersection from "lodash/intersection";
import flattenDeep from "lodash/flattenDeep";
import {
  downloadNonPdfInvestigationWidget,
  exportWidget,
  exportPdfWidget
} from "search/ExportWidgetAction/util/WidgetExportUtils";
import AggregationWidget from "views/logic/aggregationbuilder/AggregationWidget";
import EventsWidget from "views/logic/widgets/events/EventsWidget";
import startDownload from "views/components/export/startDownload";
import ExportStrategy from "views/components/export/ExportStrategy";
import InvestigationsWidget from "security-app/components/Investigations/plugin/SearchWidgets/logic/InvestigationsWidget";
import MessagesWidget from "views/logic/widgets/MessagesWidget";
import LogViewWidget from "logview/logic/LogViewWidget";
export const MAXIMUM_GRID_SIZE = 12;
const potentiallyLongFields = ["message"];
const getNonTableOrientation = (width) => {
  if (width > MAXIMUM_GRID_SIZE / 2) return "landscape";
  return "portrait";
};
const getTableOrientation = (fields, width) => {
  const hasLongValues = fields && intersection(fields, potentiallyLongFields).length > 0;
  if (fields && (hasLongValues && fields.length > 2 || !hasLongValues && fields.length > 9)) return "landscape";
  return getNonTableOrientation(width);
};
const getWidgetPosition = (view, currentQuery, widget) => {
  let widgetPosition = view.state.get(currentQuery.id).widgetPositions[widget.id];
  if (widgetPosition.width === Infinity) {
    widgetPosition = widgetPosition.toBuilder().width(MAXIMUM_GRID_SIZE).build();
  }
  return widgetPosition.toJSON();
};
const exportByRenderPage = ({
  widget,
  extension,
  view,
  widgetResult,
  currentUser,
  currentQuery,
  widgetTitle,
  executionState,
  orientation = void 0
}) => {
  var _a;
  const searchTypeId = (_a = view.widgetMapping) == null ? void 0 : _a.get(widget.id).first();
  const searchId = view.search.id;
  const position = getWidgetPosition(view, currentQuery, widget);
  return exportPdfWidget(
    widgetTitle,
    omit(
      {
        ...widget.toJSON(),
        search_id: searchId,
        search_type_id: searchTypeId,
        creator_user_id: currentUser.id,
        query_id: currentQuery.id,
        widget_type: "exportable_search_widget",
        parameter_values: executionState.parameterBindings,
        timezone: currentUser.timezone,
        orientation: orientation != null ? orientation : getNonTableOrientation(position.width),
        position
      },
      ["id"]
    ),
    widgetResult,
    extension,
    "search_type_data"
  );
};
const exportInvestigationsWidgetNonPdf = ({
  widget,
  extension,
  widgetTitle
}) => downloadNonPdfInvestigationWidget(widgetTitle, widget, extension);
const downloadByExportStrategy = ({
  widget,
  extension,
  view,
  widgetResult,
  currentUser,
  currentQuery,
  executionState,
  payload
}) => {
  const widgetTitle = view.getWidgetTitleByWidget(widget);
  const strategies = {
    [AggregationWidget.type]: () => {
      var _a;
      const isTable = ((_a = widget == null ? void 0 : widget.config) == null ? void 0 : _a.visualization) === "table";
      const series = widget.config.series.map((s) => s.function);
      const allFields = flattenDeep([widget.config.rowPivots.map(({ fields }) => fields), series]);
      const { width } = getWidgetPosition(view, currentQuery, widget);
      const orientation = isTable ? getTableOrientation(allFields, width) : getNonTableOrientation(width);
      switch (extension) {
        case "pdf":
          return {
            downloadFile: () => exportByRenderPage({
              widget,
              extension,
              orientation,
              view,
              widgetResult: widgetResult == null ? void 0 : widgetResult.chart,
              currentUser,
              currentQuery,
              widgetTitle,
              executionState
            })
          };
        default:
          return {
            downloadFile: () => exportWidget(widgetTitle, widgetResult == null ? void 0 : widgetResult.chart, extension)
          };
      }
    },
    [EventsWidget.type]: () => {
      switch (extension) {
        case "pdf":
          return {
            downloadFile: () => exportByRenderPage({
              widget,
              extension,
              view,
              widgetResult: null,
              currentUser,
              currentQuery,
              widgetTitle,
              executionState
            })
          };
        default:
          return {
            downloadFile: () => {
              const { downloadFile: downloadFn } = ExportStrategy.createExportStrategy(view.type);
              const selectedFields = widget.config.fields.toArray().map((field) => ({ field }));
              return startDownload(extension, downloadFn, view, executionState, widget, selectedFields, void 0, {});
            }
          };
      }
    },
    [InvestigationsWidget.type]: () => {
      const investigationWidget = widget;
      switch (extension) {
        case "pdf":
          return {
            downloadFile: () => exportByRenderPage({
              widget,
              extension,
              view,
              widgetResult: null,
              currentUser,
              currentQuery,
              widgetTitle,
              executionState
            })
          };
        default:
          return {
            downloadFile: () => exportInvestigationsWidgetNonPdf({ widget: investigationWidget, widgetTitle, extension })
          };
      }
    },
    [MessagesWidget.type]: () => {
      switch (extension) {
        case "pdf":
          return {
            downloadFile: () => {
              const widgetConfig = widget.config.toBuilder().fields(payload.fields_in_order).build();
              const { width } = getWidgetPosition(view, currentQuery, widget);
              return exportByRenderPage({
                widget: widget.toBuilder().config(widgetConfig).build(),
                orientation: getTableOrientation(payload.fields_in_order, width),
                extension,
                view,
                widgetResult: null,
                currentUser,
                currentQuery,
                widgetTitle,
                executionState
              });
            }
          };
        default:
          return null;
      }
    },
    [LogViewWidget.type]: () => {
      const widgetConfig = widget.config.toBuilder().fields(payload.fields_in_order).build();
      const { width } = getWidgetPosition(view, currentQuery, widget);
      switch (extension) {
        case "pdf":
          return {
            downloadFile: () => exportByRenderPage({
              widget: widget.toBuilder().config(widgetConfig).build(),
              orientation: getTableOrientation(payload.fields_in_order, width),
              extension,
              view,
              widgetResult: null,
              currentUser,
              currentQuery,
              widgetTitle,
              executionState
            })
          };
        default:
          return null;
      }
    }
  };
  const downLoadFn = strategies[widget.type]().downloadFile;
  return downLoadFn();
};
export default downloadByExportStrategy;
